import styled, { keyframes, css } from "styled-components";
import { FlexWrapper } from "../AdAssistant.styled";
import { motion } from "framer-motion";

const confettiAnimation = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-50px);
  }
`;

export const CartLayout = styled.div`
  display: grid;
  place-items: center;
  justify-content: start;
  align-items: center;
  justify-items: center;
  gap: 20px;
`;

export const CartBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  height: fit-content;
  width: 100%;
  min-width: 330px;
  max-width: 330px;
  padding: 10px 10px 20px;
  gap: 14px;
  border-radius: 5px;
  border: 1px solid var(--primary-dark-100);
  background: #fff;
  position: relative;
  z-index: 1;

  @media screen and (min-width: 1920px) and (min-device-pixel-ratio: 2),
    screen and (min-width: 1920px) and (min-resolution: 192dpi) {
    min-width: 300px;
  }
`;

export const MinBadgetWrapper = styled(FlexWrapper)`
  opacity: ${({ show }) => (show ? 1 : 0)};
  max-height: ${({ show }) => (show ? "30px" : "0px")};
  transition: all 0.3s ease-in-out;
  overflow: visible;
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 1;
`;

export const MinBadget = styled.div`
  width: 19px;
  height: 19px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1px 4px;
  position: relative;
  transition: all 0.3s ease-in-out;
  opacity: 1;
  z-index: 2;

  font-size: var(--fs-sm);

  ${({ $isMinBadget, $reachedMinimum }) =>
    $isMinBadget &&
    css`
      background: ${$reachedMinimum ? "#D4D8F9" : "#36F99F"};
      color: ${$reachedMinimum ? "#4056F4" : "black"};
      width: auto;
      height: 19px;
      border-radius: 2px;
      position: relative;
      z-index: 1;

      ::after {
        content: "";
        position: absolute;
        bottom: -12px;
        left: 50%;
        transform: translateX(-50%);
        border-width: 6px;
        border-style: solid;
        border-color: ${$reachedMinimum ? "#D4D8F9" : "#36F99F"} transparent transparent transparent;
        transition: all 0.3s ease-in-out;
      }
    `}
`;

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(30, 34, 255, 0.5);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(30, 34, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(30, 34, 255, 0);
  }
`;

export const CartItem = styled.div`
  position: relative;
  width: 19px;
  height: 19px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $isRequired, $isSelected, $reachedMinimum, $isNext }) => {
    if ($reachedMinimum && $isSelected)
      return css`
        background-color: var(--primary-green);
      `;
    if (!$reachedMinimum && $isSelected)
      return css`
        background-color: var(--primary-dark-200);
      `;
    if ($isRequired || $isNext)
      return css`
        background-color: var(--primary-dark-200);
      `;
    return css`
      background-color: var(--primary-dark-100);
    `;
  }}

  ${({ $isCurrent }) =>
    $isCurrent &&
    css`
      animation: ${pulseAnimation} 1.5s infinite;
      border: 2px solid var(--primary-blue);
    `}

  svg[id="check-assistant-icon"] {
    cursor: pointer;
  }

  &:hover {
    ${({ $isCurrent, $isSelected, $isNext }) =>
      ($isCurrent || $isSelected || $isNext) &&
      css`
        border: 2px solid var(--primary-blue);
      `}
  }
`;

export const SectionSummary = styled.div`
  display: flex;
  gap: ${({ gap }) => gap}px;
  align-items: ${({ alignItems }) => alignItems || "center"};
  width: 100%;
  color: var(--primary-blue-300);
  font-size: var(--fs-base);
  line-height: var(--lh-base);
  font-style: normal;
  font-weight: 400;
`;

export const SectionTitle = styled(SectionSummary)`
  color: var(--primary-black);
  font-size: var(--fs-base);
  line-height: var(--lh-base);
  font-weight: 600;
`;

export const Divider = styled.div`
  width: 2px;
  height: 17px;
  background-color: lightgray;
`;

export const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--primary-dark-200);
`;

export const ConfettiContainer = styled.div`
  position: absolute;
  bottom: 80%;
  width: 290px;
  height: 280px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${confettiAnimation} 3s ease-in-out;
  animation-fill-mode: forwards;
  z-index: 9999;
`;

export const SectionCounter = styled.div`
  color: var(--primary-blue);
  font-size: var(--fs-lg);
  font-weight: 500;
`;

export const CartCounter = styled.div`
  color: var(--primary-dark);
  font-size: var(--fs-base);
  font-weight: 500;
`;

export const AdCounterWrapper = styled(FlexWrapper)`
  width: 100%;
  padding: ${({ $hasMinBadgets }) => ($hasMinBadgets ? "10px 10px 20px" : "15px 10px 20px")};
  border-radius: 5px;
  background-color: var(--primary-dark-50);
  transition: padding 0.3s ease-in-out;
  position: relative;
`;

export const ScrollContainer = styled(motion.div)`
  width: 100%;
  overflow: hidden;
  padding: 10px 0;
  position: relative;
  display: flex;
  align-items: center;
`;

export const ItemsContainer = styled(motion.div)`
  display: flex;
  gap: 10px;
  padding: 0 30px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const CartItemWrapper = styled(motion.div)`
  flex: 0 0 auto;
  user-select: none;
`;

export const SliderArrow = styled.div`
  position: absolute;
  z-index: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--primary-blue);
  transition: all 0.2s ease;
  background-color: var(--primary-dark-50);

  &:disabled {
    color: var(--primary-dark-20);
    cursor: not-allowed;
  }

  ${({ $isFirstAdBox }) =>
    $isFirstAdBox &&
    css`
      opacity: 0;
      cursor: default;
      svg {
        color: var(--primary-dark-20);
      }
    `}

  ${({ $isLastAdBox }) =>
    $isLastAdBox &&
    css`
      cursor: not-allowed;
    `}

  ${({ direction }) =>
    direction === "left"
      ? `
    left: 0;
  `
      : `
    right: 0;
  `}
`;
